'use strict';

import React from "react";
import { action, observable, configure, makeObservable } from "mobx";
import moment from "moment";
configure({enforceActions: 'always'});

import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {buildRegistrationUrl} from "@uw-it-sis/lib-react-myplan/lib/RegistrationUtils";
import {isEmpty, isFalse} from "@uw-it-sis/lib-react/lib/Utils";
import termStore from "@uw-it-sis/lib-react/lib/TermStore";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {RegEx} from "@uw-it-sis/lib-react/lib/ValidationUtils";

class RegisterButtonStore {
    state = States.initial;

    data = new Map();

    /** SAMPLE data
     * {
     *      "12345": {
     *          "activityCode": "MATH 101 A",
     *          "entryCode": "",
     *          "isEntryCodeRequired": true,
     *          "isBlockReg": false,
     *          "isBlockRegParent": false
     *      }
     * }
     */

    constructor() {
        makeObservable(this, {
            state: observable,
            data: observable,
            mount: action,
            initializeData: action,
            updateEntryCode: action
        });

        this.termId = "";
        this.periodOneStartDate = undefined;
        this.lastAddDate = undefined;
    }

    mount(slnData, termId) {
        this.state = States.pending;
        this.data = slnData;
        this.termId = termId;
        this.state = States.done;
    }

    initializeData(termId) {
        this.termId = termId;

        let lastAddDate = undefined;
        // Get the term data for the selected term id (via component prop)
        let selectedTerm = termStore.get('publishedTerms').find(function (term) {
            return term.id === termId;
        }.bind(this));

        if (selectedTerm) {
            this.periodOneStartDate = TermUtils.findPeriodOnePriorityRegDate(selectedTerm);
            selectedTerm.academicCalendar.map(entry => {
                if (entry.key === 'last_add_day') {
                    // use a time of 11:59pm to check that today's date is past the complete day of the last add day
                    lastAddDate = moment(entry.date + " 23:59", "YYYY-MM-DD hh:mm");
                }
            });

            if (lastAddDate) {
                this.lastAddDate = lastAddDate;
            }
        }
    }

    updateEntryCode(sln, entryCode) {
        if (this.data.hasOwnProperty(sln)) {
            this.data[sln].entryCode = entryCode;
        } else {
            // This shouldn't happen, data mismatch, each SLN should be a key of the data map.
            this.state = States.error;
        }
    }

    buildRegisterUrl() {
        if (appStore.isAdviser()) {
            return null;
        }

        let baseRegUrl = appStore.getAppAttribute('sdb_registration_url');

        let slnList = [];
        let slnToEntryCodes = {};

        for (const [key, slnData] of Object.entries(this.data)) {
            // Build the list of SLNs with their entry codes
            if (
                this._isSlnDataObject(slnData) &&
                slnData.entryCode
            ) {
                slnToEntryCodes[key] = slnData.entryCode;
            }

            // Build a list of SLNs without block reg children
            if ( !slnData.isBlockReg || (slnData.isBlockReg && slnData.isBlockRegParent) ) {
                slnList.push(key);
            }
        }

        // build the SLNs out of data

        return buildRegistrationUrl(baseRegUrl, appStore.getStudentSystemKey(),
            this.termId, slnList, slnToEntryCodes, "mp_schedule_builder");
    }

    isDisabled() {
        // disable if entry codes are required, but values are not all 5 digit codes
        let hasEntryCodeIssue = false;

        let slnDataList = Object.values(this.data);

        for (const slnData of slnDataList) {
            if (
                this._isSlnDataObject(slnData) &&
                isFalse(RegEx.sln.test(slnData.entryCode))
            ) {
                hasEntryCodeIssue = true;
            }

            if (hasEntryCodeIssue) { break; }
        }

        return appStore.isAdviser() || hasEntryCodeIssue || isEmpty(this.data);
    }

    getMissingEntryCodes() {
        let missingCodes = [];

        let slnDataList = Object.values(this.data);

        for (const slnData of slnDataList) {
            if (
                this._isSlnDataObject(slnData) &&
                isFalse(RegEx.sln.test(slnData.entryCode))
            ) {
                missingCodes.push(slnData.activityCode);
            }
        }

        return missingCodes;
    }

    getSlnDataCount() {
        return Object.keys(this.data).length;
    }

    showButton() {
        let registrationOpen = false;
        let now = moment(appStore.getNow());

        if ( this.periodOneStartDate && this.lastAddDate) {
            registrationOpen = now.isBetween(this.periodOneStartDate, this.lastAddDate);
        }

        return registrationOpen;
    }

    getLastAddDate(format = "dddd, MMM D") {
        return this.lastAddDate.format(format);
    }

    _isSlnDataObject(slnData) {

        if (slnData !== undefined && !isEmpty(slnData) &&
            slnData.hasOwnProperty("isEntryCodeRequired") &&
            slnData.isEntryCodeRequired &&
            slnData.hasOwnProperty("entryCode") ) {

            return true;
        }

        return false;
    }
}

export default new RegisterButtonStore();